import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Editor } from "primereact/editor";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import axios from "axios";
import { Api } from "../../../services/api";
import './EmailDialog.css'
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

const EmailDialog = ({
  visible,
  onHide,
  showName,
  showEmail,
  showId,
  allSupervisors,
  toast,
}) => {
  const { instance, accounts } = useMsal();
  const defaultSignature = "\nThank you,\nBuilder Playbook Team";
  const [customSubject, setCustomSubject] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [signature, setSignature] = useState(localStorage.getItem('emailSignature') || defaultSignature);
  const [signatureEditorMode, setSignatureEditorMode] = useState("visual");
  const [selectedCCList, setSelectedCCList] = useState([]);
  const [emailThreads, setEmailThreads] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null); // stores thread/message being replied to
  const [expandedMessages, setExpandedMessages] = useState({});
  const [loading, setLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const signatureImageInputRef = useRef(null);

  const hasWebmailHostedSignatureImage = /<img[^>]+src=["']https:\/\/(?:mail\.google\.com|outlook\.office\.com|outlook\.office365\.com|attachments\.office\.net)/i.test(signature || "");

  const signaturePreviewHtml = /<[^>]+>/.test(signature || "")
    ? signature
    : (signature || defaultSignature).replace(/\n/g, '<br/>');

  const normalizeSignatureMarkup = (markup) => {
    if (!markup) {
      return "";
    }

    return markup
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/<style[\s\S]*?<\/style>/gi, "")
      .replace(/<!--([\s\S]*?)-->/g, "")
      .trim();
  };

  const replaceSignatureImageSource = (markup, nextSource) => {
    const normalizedMarkup = markup || "";

    if (!/<[^>]+>/.test(normalizedMarkup)) {
      return `<p><img src="${nextSource}" alt="signatureImage" /></p><p>${normalizedMarkup.replace(/\n/g, '<br/>')}</p>`;
    }

    const imagePattern = /<img([^>]*?)src=["'][^"']+["']([^>]*?)>/i;
    if (imagePattern.test(normalizedMarkup)) {
      return normalizedMarkup.replace(imagePattern, `<img$1src="${nextSource}"$2>`);
    }

    return `${normalizedMarkup}<p><img src="${nextSource}" alt="signatureImage" /></p>`;
  };

  const handleSignatureImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(new Error("Image upload failed"));
        reader.readAsDataURL(file);
      });

      if (!dataUrl.startsWith("data:image/")) {
        throw new Error("Only image files are supported");
      }

      setSignature((currentSignature) => replaceSignatureImageSource(currentSignature || defaultSignature, dataUrl));
      toast.current.show({
        severity: "success",
        summary: "Signature Image Added",
        detail: "The signature image was embedded and will be sent inline.",
        life: 3500,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        detail: error.message || "Unable to add signature image.",
        life: 3500,
      });
    } finally {
      event.target.value = "";
    }
  };

  const fetchImageAsInlineAttachment = async (imageUrl, attachmentIndex) => {
    const response = await fetch(imageUrl);

    if (!response.ok) {
      throw new Error(`Unable to fetch image: ${response.status}`);
    }

    const imageBlob = await response.blob();

    const contentBytes = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result || '';
        const base64Content = String(result).split(',')[1] || '';
        if (!base64Content) {
          reject(new Error('Image conversion failed'));
          return;
        }
        resolve(base64Content);
      };
      reader.onerror = () => reject(new Error('Image conversion failed'));
      reader.readAsDataURL(imageBlob);
    });

    const mimeType = imageBlob.type || 'image/png';
    const extension = mimeType.split('/')[1]?.split('+')[0] || 'png';
    const contentId = `signature-image-${attachmentIndex}-${Date.now()}`;

    return {
      attachment: {
        '@odata.type': '#microsoft.graph.fileAttachment',
        name: `signature-image-${attachmentIndex}.${extension}`,
        contentType: mimeType,
        contentBytes,
        isInline: true,
        contentId,
        contentLocation: contentId,
      },
      contentId,
    };
  };

  const prepareSignatureForEmail = async (rawSignature) => {
    if (!rawSignature) {
      return {
        html: "",
        attachments: [],
        removedImages: 0,
        unresolvedRemoteImages: 0,
      };
    }

    const normalizedSignature = normalizeSignatureMarkup(rawSignature);
    const looksLikeHtml = /<[^>]+>/.test(normalizedSignature);

    if (!looksLikeHtml) {
      return {
        html: normalizedSignature.replace(/\n/g, '<br/>'),
        attachments: [],
        removedImages: 0,
        unresolvedRemoteImages: 0,
      };
    }

    try {
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(normalizedSignature, 'text/html');
      const root = parsedDocument.body;
      const attachments = [];
      let removedImages = 0;
      let unresolvedRemoteImages = 0;

      root.querySelectorAll('script, style, meta, title, link, xml, o\\:p').forEach((node) => node.remove());

      root.querySelectorAll('*').forEach((element) => {
        ['class', 'id', 'data-ogsb', 'data-outlook-id'].forEach((attribute) => {
          element.removeAttribute(attribute);
        });
      });

      const images = Array.from(root.querySelectorAll('img'));

      for (const image of images) {
        const rawSrc = (image.getAttribute('src') || '').trim();
        const src = rawSrc.toLowerCase();

        if (src.startsWith('data:image/')) {
          const match = rawSrc.match(/^data:(image\/[a-z0-9.+-]+);base64,(.+)$/i);
          if (!match) {
            removedImages += 1;
            image.remove();
            continue;
          }

          const mimeType = match[1];
          const contentBytes = match[2].replace(/\s/g, '');
          const extension = mimeType.split('/')[1]?.split('+')[0] || 'png';
          const contentId = `signature-image-${attachments.length + 1}-${Date.now()}`;

          attachments.push({
            '@odata.type': '#microsoft.graph.fileAttachment',
            name: `signature-image-${attachments.length + 1}.${extension}`,
            contentType: mimeType,
            contentBytes,
            isInline: true,
            contentId,
            contentLocation: contentId,
          });

          image.setAttribute('src', `cid:${contentId}`);
          continue;
        }

        if (!src || src.startsWith('file:') || src.startsWith('blob:')) {
          removedImages += 1;
          image.remove();
          continue;
        }

        if (src.startsWith('cid:')) {
          continue;
        }

        if (/^https?:\/\//i.test(rawSrc)) {
          try {
            const { attachment, contentId } = await fetchImageAsInlineAttachment(rawSrc, attachments.length + 1);
            attachments.push(attachment);
            image.setAttribute('src', `cid:${contentId}`);
          } catch (error) {
            unresolvedRemoteImages += 1;
          }
          continue;
        }

        if (!/^https?:\/\//i.test(rawSrc)) {
          removedImages += 1;
          image.remove();
        }
      }

      root.querySelectorAll('table, tbody, tr, td, div, p, span').forEach((element) => {
        const hasRenderableChild = Array.from(element.querySelectorAll('img, br, hr')).length > 0;
        const textContent = (element.textContent || '').replace(/\u00a0/g, ' ').trim();
        if (!hasRenderableChild && textContent === '') {
          element.remove();
        }
      });

      return {
        html: root.innerHTML.trim(),
        attachments,
        removedImages,
        unresolvedRemoteImages,
      };
    } catch (error) {
      console.error('Signature normalization error:', error);
      return {
        html: normalizedSignature,
        attachments: [],
        removedImages: 0,
        unresolvedRemoteImages: 0,
      };
    }
  };

  const extractSignatureFromEmailBody = (emailBody) => {
    if (!emailBody) {
      return "";
    }

    let cleanedBody = normalizeSignatureMarkup(emailBody);

    const bodyMatch = cleanedBody.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    if (bodyMatch) {
      cleanedBody = bodyMatch[1];
    }

    const signatureBlockMatch = cleanedBody.match(/<div[^>]*(class|id)="[^"]*(signature|x_signature)[^"]*"[^>]*>[\s\S]*?<\/div>/i);
    if (signatureBlockMatch) {
      return normalizeSignatureMarkup(signatureBlockMatch[0]);
    }

    const replySeparators = [
      /<div[^>]*class="gmail_quote"[^>]*>[\s\S]*$/i,
      /<div[^>]*id="divRplyFwdMsg"[^>]*>[\s\S]*$/i,
      /<hr[^>]*id="replySplit"[^>]*>[\s\S]*$/i,
      /<hr[^>]*>[\s\S]*$/i,
      /On\s.+?wrote:[\s\S]*$/i,
      /From:\s.+?Sent:\s.+/is
    ];

    replySeparators.forEach((separator) => {
      cleanedBody = cleanedBody.replace(separator, "").trim();
    });

    const signatureMarkers = [
      "Thank you,",
      "Thanks,",
      "Best regards,",
      "Regards,",
      "Sincerely,",
      "Cheers,",
      "Kind regards,",
      "Sent from my"
    ];

    const lowerBody = cleanedBody.toLowerCase();
    let markerIndex = -1;

    signatureMarkers.forEach((marker) => {
      const index = lowerBody.lastIndexOf(marker.toLowerCase());
      if (index > markerIndex) {
        markerIndex = index;
      }
    });

    if (markerIndex >= 0) {
      return normalizeSignatureMarkup(cleanedBody.substring(markerIndex));
    }

    const plainTextLines = cleanedBody
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<\/div>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    if (plainTextLines.length === 0) {
      return "";
    }

    return plainTextLines.slice(Math.max(plainTextLines.length - 6, 0)).join("\n");
  };

  const saveSignature = () => {
    localStorage.setItem('emailSignature', signature || defaultSignature);
    toast.current.show({
      severity: 'success',
      summary: 'Signature Saved',
      detail: 'Your email signature has been saved for future emails.',
      life: 3000
    });
  };

  const resetSignature = () => {
    setSignature(defaultSignature);
    localStorage.setItem('emailSignature', defaultSignature);
    toast.current.show({
      severity: 'info',
      summary: 'Signature Reset',
      detail: 'The default signature has been restored.',
      life: 3000
    });
  };

  const loginToMicrosoft = async () => {
      try {
          await instance.loginPopup({
              scopes: ["Mail.Read", "Mail.Send", "Mail.ReadWrite"],
              prompt: "select_account"
          });
      } catch (e) {
          console.error(e);
          toast.current.show({ severity: 'error', detail: "Microsoft Login Failed: " + e.message });
      }
  };

  const getAccessToken = async () => {
    if (accounts.length === 0) {
        await loginToMicrosoft();
        if (accounts.length === 0) return null; // Still not logged in
    }

    const request = {
      scopes: ["Mail.Read", "Mail.Send", "Mail.ReadWrite"],
      account: accounts[0]
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        return instance.acquireTokenPopup(request).then((response) => {
          return response.accessToken;
        }).catch((error) => {
          toast.current.show({ severity: 'error', detail: "Error acquiring token: " + error.message });
          return null;
        });
      } else {
        toast.current.show({ severity: 'error', detail: "Error acquiring token: " + error.message });
        return null;
      }
    }
  };

  const importSignature = async () => {
    setLoading(true);
    const token = await getAccessToken();
    if (!token) {
        setLoading(false);
        return;
    }

    try {
        const response = await fetch("https://graph.microsoft.com/v1.0/me/mailFolders/SentItems/messages?$top=1&$select=body", {
            headers: { Authorization: `Bearer ${token}` }
        });

        if (response.ok) {
            const data = await response.json();
            if (data.value && data.value.length > 0) {
            const extractedSignature = extractSignatureFromEmailBody(data.value[0].body.content);

            if (!extractedSignature) {
              toast.current.show({
                severity: 'warn',
                summary: 'Signature Not Found',
                detail: 'No reliable signature block was detected. Please save one manually below.',
                life: 5000
              });
              return;
            }

            setSignature(extractedSignature);
                toast.current.show({ 
                    severity: 'warn', 
              summary: 'Signature Imported', 
              detail: 'We imported the best signature match from your latest sent email. Please review it before saving.', 
                    life: 6000 
                });
            } else {
                toast.current.show({ severity: 'warn', detail: "No sent emails found.", life: 3000 });
            }
        }
    } catch (error) {
        toast.current.show({ severity: 'error', detail: "Error importing signature: " + error.message, life: 3000 });
    } finally {
        setLoading(false);
    }
  };


  useEffect(() => {
    if (visible && showEmail) {
      fetchEmailThreads();
      setReplyingTo(null); // reset reply when dialog opens
    }
  }, [visible, showEmail, accounts]);

  const fetchEmailThreads = async () => {
    // If we're not logged in, we can't fetch. Just return. A button will be shown in UI.
    if (!accounts || accounts.length === 0) return;
    
    setLoading(true);
    const token = await getAccessToken();
    if (!token) {
        setLoading(false);
        return; 
    }

    try {
      if (!showEmail) {
          console.warn("fetchEmailThreads: showEmail is empty");
          return;
      }
      const targetEmail = showEmail.trim().toLowerCase(); // Ensure no whitespace and consistent casing
      console.log("Fetching emails for:", targetEmail);

      // Use $search because $filter on collections (toRecipients) is not supported in Graph API v1.0
      // We use KQL (Keyword Query Language) to search all participants.
      // Note: usage of $search requires "ConsistencyLevel: eventual" header.
      const searchQuery = `"participants:${targetEmail}"`;
      
      const url = `https://graph.microsoft.com/v1.0/me/messages?$search=${encodeURIComponent(searchQuery)}&$top=50&$select=id,conversationId,subject,body,from,toRecipients,ccRecipients,sentDateTime,receivedDateTime`;
      
      console.log("Graph API URL:", url);

      const response = await fetch(url, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "ConsistencyLevel": "eventual" 
          }
      });
      
      if (!response.ok) {
          const errText = await response.text();
          console.error("Graph API Error:", response.status, errText);
          throw new Error(`Graph API returned ${response.status}: ${errText}`);
      }


      if (response.ok) {
          const data = await response.json();
          // Sort client-side because $orderby is not supported with $search
          const messages = data.value.sort((a, b) => new Date(b.receivedDateTime) - new Date(a.receivedDateTime));

          // Group by conversationId
          const grouped = {};
          messages.forEach(msg => {
              if (!grouped[msg.conversationId]) {
                  grouped[msg.conversationId] = {
                      thread_number: msg.conversationId, // unique ID
                      subject: msg.subject,
                      messages: []
                  };
              }
              
              // Map Graph message to UI structure
              const defaults = {
                  _id: msg.id,
                  direction: msg.from?.emailAddress?.address?.toLowerCase() === showEmail.toLowerCase() ? 'incoming' : 'outgoing',
                  from_email: msg.from?.emailAddress?.address,
                  to_email: msg.toRecipients?.map(r => r.emailAddress.address) || [],
                  send_at: msg.sentDateTime || msg.receivedDateTime,
                  message: msg.body.content,
                  subject: msg.subject
              };
              grouped[msg.conversationId].messages.push(defaults);
          });

          // Convert to array
          const threads = Object.values(grouped).map((t, index) => ({
              ...t,
              thread_number: index + 1 // simpler display number
          }));
          
          setEmailThreads(threads);
      }
    } catch (error) {
      console.error("Error fetching email history:", error);
      // Fallback to empty or toast error
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!customSubject || !customMessage) return;

    // TODO: Graph API Implementation
    const token = await getAccessToken();
    if (!token) return;

    // Logic to strip <p> tags from signature if duplicate
    const preparedSignature = await prepareSignatureForEmail(signature || defaultSignature);
    const finalSignature = preparedSignature.html;
    
    const emailBody = {
        message: {
            subject: customSubject,
            body: {
                contentType: "HTML",
                content: customMessage + '<br/><br/>' + finalSignature
            },
            toRecipients: [
                {
                    emailAddress: {
                        address: showEmail // Use actual email, not name
                    }
                }
            ],
            ccRecipients: selectedCCList.map(u => ({
                emailAddress: {
                    address: u.email // Ensure user object has email property
                }
            })),
            ...(preparedSignature.attachments.length
              ? { attachments: preparedSignature.attachments }
              : {})
        },
        saveToSentItems: "true"
    };

    try {
      const response = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
          method: "POST",
          headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
          },
          body: JSON.stringify(emailBody)
      });

      if (response.ok) {
          toast.current.show({
            severity: "success",
            detail: "Email sent via Microsoft Graph!",
            life: 3000,
          });

          if (preparedSignature.removedImages > 0) {
            toast.current.show({
              severity: 'warn',
              detail: 'Some signature images used unsupported local sources and were removed. Use a normal https image URL or paste a base64 image in HTML mode.',
              life: 5000,
            });
          }

          if (preparedSignature.unresolvedRemoteImages > 0) {
            toast.current.show({
              severity: 'info',
              detail: 'Some signature images could not be embedded, so the original https image URLs were kept. They will render only if the recipient can access them.',
              life: 6000,
            });
          }

          // Optimistically update UI
          const newMessage = {
            _id: Date.now().toString(),
            lead_id: showId,
            cc_list: selectedCCList.map((u) => u.email),
            message: customMessage + '<br/><br/>' + finalSignature,
            subject: customSubject,
            direction: "outgoing",
            from_email: accounts[0]?.username || "Me",
            to_email: [showEmail],
            message_id: null, // response doesn't return ID immediately
            send_at: new Date().toISOString(),
          };

          // Since we might not have conversation ID, we might just prepend to top thread or force refresh
          // For simplicity, let's refresh threads in a few seconds or add to UI manually
          // Actually, since we don't have conversationId from Graph response for sent item immediately, 
          // we can just refetch threads.
          setTimeout(() => fetchEmailThreads(), 2000);

          setCustomSubject("");
          setCustomMessage("");
          setSelectedCCList([]);
          setReplyingTo(null);

      } else {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || response.statusText);
      }
    } catch (error) {
      console.error("Error sending email:", error);
      toast.current.show({
        severity: "error",
        detail: "Failed to send email: " + error.message,
        life: 3000,
      });
    }
  };

  const dialogHeader = (
    <div className="flex align-items-center gap-2">
      <i className="pi pi-envelope text-primary text-xl"></i>
      <span className="text-xl font-semibold text-900">
        Email Correspondence with {showName}
      </span>
    </div>
  );




  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      style={{ width: "55vw", maxWidth: "800px" }}
      dismissableMask
      onHide={onHide}
      className="email-dialog"
      draggable={false}
      resizable={false}
    >
      <div className="email-dialog-container">
        {/* Show draft box if no previous emails or replying */}
        {(!emailThreads.length || replyingTo) && (
          <form
            onSubmit={handleSendEmail}
            className="flex flex-column gap-2 mb-3 p-3"
          >
            <h4 className="m-0 text-900 font-semibold" style={{ fontSize: '1rem' }}>Compose Email</h4>
            
            {/* To Field */}
            <div className="flex flex-column">
              <label className="text-sm font-medium text-700 mb-1">To</label>
              <InputText 
                  value={showName && showEmail ? `${showName} <${showEmail}>` : (showEmail || showName)} 
                  disabled 
                  className="w-full bg-gray-50 custom-input-height" 
              />
            </div>

            {/* CC Field */}
            <div className="flex flex-column">
              <label className="text-sm font-medium text-700 mb-1">CC</label>
              <MultiSelect
                value={selectedCCList}
                onChange={(e) => setSelectedCCList(e.value)}
                options={allSupervisors}
                optionLabel={(e) =>
                  `${e.first_name} ${e.last_name} - ${e.role_name}`
                }
                display="chip"
                filter
                placeholder="Select CC recipients"
                className="w-full custom-input-height"
                pt={{
                    root: { style: { minHeight: '38px' } }
                }}
              />
            </div>

            {/* Subject */}
            <div className="flex flex-column">
              <label htmlFor="customSubject" className="text-sm font-medium text-700 mb-1">Subject</label>
              <InputText
                id="customSubject"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                placeholder="Enter email subject"
                className="w-full custom-input-height"
              />
            </div>

            {/* Message Editor */}
            <div className="flex flex-column">
              <label htmlFor="customMessage" className="text-sm font-medium text-700 mb-1">Message</label>
              <Editor
                id="customMessage"
                value={customMessage}
                onTextChange={(e) => setCustomMessage(e.htmlValue)}
                style={{ height: "200px" }}
                pt={{
                    content: { style: { borderRadius: '0 0 6px 6px' } },
                    toolbar: { style: { borderRadius: '6px 6px 0 0' } }
                }}
              />
            </div>

            {/* Signature Area */}
            <div className="flex flex-column mt-3">
                <div className="flex justify-content-between align-items-center mb-1">
                    <div>
                      <label className="text-sm font-medium text-700">Signature</label>
                      <p className="email-signature-note m-0 mt-1">
                        Microsoft does not provide a direct signature API. You can save a signature here or try importing a best-effort version from your latest sent email.
                      </p>
                    </div>
                    <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                      <input
                        ref={signatureImageInputRef}
                        type="file"
                        accept="image/*"
                        className="email-signature-image-input"
                        onChange={handleSignatureImageUpload}
                      />
                      <Button
                          type="button"
                          label="Upload Image"
                          icon="pi pi-image"
                          className="p-button-text p-button-sm p-0 text-xs"
                          onClick={() => signatureImageInputRef.current?.click()}
                          disabled={loading}
                      />
                      <Button 
                          type="button" 
                          label="Try Import" 
                          icon="pi pi-download" 
                          className="p-button-text p-button-sm p-0 text-xs" 
                          onClick={importSignature}
                          disabled={loading}
                      />
                      <Button 
                          type="button" 
                          label="Reset" 
                          icon="pi pi-refresh" 
                          className="p-button-text p-button-sm p-0 text-xs" 
                          onClick={resetSignature}
                          disabled={loading}
                      />
                      <Button 
                          type="button" 
                          label="Save Signature" 
                          icon="pi pi-save" 
                          className="p-button-text p-button-sm p-0 text-xs font-semibold" 
                          onClick={saveSignature}
                          disabled={loading}
                      />
                    </div>
                </div>
                <div className="email-signature-mode-toggle mb-2">
                  <Button
                    type="button"
                    label="Visual"
                    className={signatureEditorMode === "visual" ? "p-button-sm" : "p-button-sm p-button-text"}
                    onClick={() => setSignatureEditorMode("visual")}
                  />
                  <Button
                    type="button"
                    label="HTML Source"
                    className={signatureEditorMode === "html" ? "p-button-sm" : "p-button-sm p-button-text"}
                    onClick={() => setSignatureEditorMode("html")}
                  />
                </div>

                {signatureEditorMode === "visual" ? (
                  <Editor
                    value={signature}
                    onTextChange={(e) => setSignature(e.htmlValue || "")}
                    style={{ height: "160px" }}
                    pt={{
                      content: { style: { borderRadius: '0 0 6px 6px' } },
                      toolbar: { style: { borderRadius: '6px 6px 0 0' } }
                    }}
                  />
                ) : (
                  <textarea 
                      className="p-inputtext p-component w-full border-round-md email-signature-source" 
                      rows={8} 
                      value={signature} 
                      onChange={(e) => setSignature(e.target.value)}
                      placeholder="Paste raw HTML signature here"
                  />
                )}

                {hasWebmailHostedSignatureImage && (
                  <div className="email-signature-warning mt-2">
                    The current signature image uses a Gmail or Outlook attachment URL. Replace it with Upload Image so it can be embedded in sent emails.
                  </div>
                )}

                <div className="email-signature-preview mt-3">
                  <div className="email-signature-preview-label">Signature Preview</div>
                  <div
                    className="email-signature-preview-surface"
                    dangerouslySetInnerHTML={{ __html: signaturePreviewHtml }}
                  />
                </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-content-end gap-2 pt-2 mt-2">
              <Button
                label="Cancel"
                type="button"
                outlined
                severity="secondary"
                onClick={() => setReplyingTo(null)}
                className="px-3 py-2"
                style={{ fontSize: '0.9rem' }}
              />
              <Button
                label={accounts.length === 0 ? "Sign in to Send" : "Send Email"}
                icon={accounts.length === 0 ? "pi pi-sign-in" : "pi pi-send"}
                type="button"
                onClick={(e) => {
                    if (accounts.length === 0) {
                        getAccessToken();
                    } else {
                        handleSendEmail(e);
                    }
                }}
                className="customSave-button px-3 py-2"
                style={{ fontSize: '0.9rem' }}
                disabled={accounts.length > 0 && (!customSubject || !customMessage || !showEmail || loading)}
              />
            </div>
          </form>
        )}

        {/* Divider */}
        {emailThreads.length > 0 && <div className="flex align-items-center gap-2 my-3">
            <div className="flex-grow-1 border-top-1 surface-border"></div>
            <span className="text-500 font-medium" style={{ fontSize: '0.75rem' }}>HISTORY</span>
            <div className="flex-grow-1 border-top-1 surface-border"></div>
        </div>}

        {/* Email Threads */}
        <div className="email-history-container max-h-30rem overflow-auto">
            {accounts.length === 0 ? (
                <div className="text-center p-5 surface-50 border-round">
                    <i className="pi pi-microsoft text-3xl text-primary mb-3"></i>
                    <p className="text-700 m-0 mb-3 font-semibold">Sign in to view email history & send messages</p>
                    <Button 
                        label="Sign in with Microsoft" 
                        icon="pi pi-sign-in" 
                        className="p-button-outlined"
                        onClick={getAccessToken} 
                    />
                </div>
            ) : loading ? (
                 <div className="flex justify-content-center p-4">
                     <i className="pi pi-spin pi-spinner text-3xl"></i>
                 </div>
            ) : emailThreads.length === 0 ? (
            <div className="text-center p-4 surface-50 border-round">
                <i className="pi pi-inbox text-3xl text-400 mb-2"></i>
                <p className="text-500 m-0" style={{ fontSize: '0.9rem' }}>No previous email history found for {showEmail}.</p>
            </div>
            ) : (
                [...emailThreads].reverse().map((thread) => (
                    <div key={thread.thread_number} className="email-thread-card">
                        <div className="flex align-items-center justify-content-between mb-2 pb-2 border-bottom-1 surface-border">
                            <h6 className="m-0 text-900 font-semibold" style={{ fontSize: '0.95rem' }}>
                            {thread.subject}
                            </h6>
                            <span className="text-500 bg-gray-100 px-2 py-1 border-round" style={{ fontSize: '0.75rem' }}>Thread #{thread.thread_number}</span>
                        </div>

                        {[...thread.messages].reverse().map((msg, i) => (
                        <div
                            key={i}
                            className={`email-message-card ${msg.direction === "incoming" ? "incoming" : "outgoing"}`}
                        >
                            <div className="email-meta">
                            <span className="font-medium text-700" style={{ fontSize: '0.85rem' }}>
                                {msg.direction === "outgoing" ? "You sent to " : "Received from "}
                                <span className="text-900 font-semibold">
                                    {msg.direction === "outgoing"
                                    ? (msg.to_email && msg.to_email.length > 0 ? msg.to_email.join(", ") : "Unknown")
                                    : (msg.from_email || "Unknown")}
                                </span>
                            </span>
                            <span style={{ fontSize: '0.8rem' }}>{new Date(msg.send_at).toLocaleString()}</span>
                            </div>

                            <div className="email-body" style={{ fontSize: '0.9rem' }}>
                            <div
                                dangerouslySetInnerHTML={{
                                __html: expandedMessages[msg._id]
                                    ? msg.message
                                    : (msg.message ? msg.message.split(" ").slice(0, 40).join(" ") +
                                    (msg.message.split(" ").length > 40 ? "..." : "") : "No Content"),
                                }}
                            />

                            {msg.message && msg.message.split(" ").length > 40 && (
                                <Button
                                label={
                                    expandedMessages[msg._id]
                                    ? "Show less"
                                    : "Show more"
                                }
                                link
                                className="p-0 mt-2"
                                style={{ fontSize: '0.85rem' }}
                                onClick={() => toggleExpand(msg._id)}
                                />
                            )}
                            </div>

                            {/* ✅ Reply button next to latest message only */}
                            {!replyingTo && i === 0 && (
                            <div className="mt-2 pt-2 border-top-1 surface-border text-right">
                                <Button
                                label="Reply to Thread"
                                icon="pi pi-reply"
                                size="small"
                                outlined
                                style={{ fontSize: '0.85rem' }}
                                onClick={() => {
                                    setReplyingTo(thread);
                                    setCustomSubject(`Re: ${thread.subject}`);
                                }}
                                />
                            </div>
                            )}
                        </div>
                        ))}
                    </div>
                ))
            )}
        </div>
      </div>
    </Dialog>
  );
};
export default EmailDialog;
